import { useState, useEffect, useMemo } from "react";
import { T, UI, FONT } from "../constants/tokens";
import { fetchCalendarEvents } from "../utils/calendar";
import { dateNumeric } from "../utils/formatDate";
import HoldingBadge from "./HoldingBadge";
import LTR from "./LTR";
import Icon from "./Icon";
import { Skeleton, Loading } from "./Skeleton";

// ─── "Happening in the market this week" lens ────────────────────────────────
// Pure facts, 7 days forward: earnings and IPOs from Finnhub's free calendar,
// and the scheduled macro releases (FRED's release calendar) and FOMC
// meetings (the Federal Reserve's schedule) · each row names its source. No
// ranking, no picks. Reuses the fetchCalendarEvents pipeline (utils/calendar.js).
// Sprint 21: inside "תמונת השוק" on the market intelligence page.

const openStock = (symbol) =>
  symbol && window.dispatchEvent(new CustomEvent("si-open-stock", { detail: { symbol } }));

function range7() {
  const iso = (d) => d.toISOString().slice(0, 10);
  const today = new Date();
  const to = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
  return { from: iso(today), to: iso(to) };
}

const TYPE_META = {
  earnings: { label: "דוח רבעוני", icon: "chart",  color: UI.categorical[0] },
  ipo:      { label: "הנפקה",      icon: "rocket", color: UI.categorical[1] },
  macro:    { label: "פרסום כלכלי", icon: "bank",   color: UI.categorical[2] },
};

function Row({ ev, portfolio, watchlist, isHe }) {
  const meta = TYPE_META[ev.type] || TYPE_META.earnings;
  const sym = ev.symbols?.[0] || null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: `1px solid ${UI.line.hairline}` }}>
      <div style={{ flexShrink: 0, textAlign: "center", minWidth: 42 }}>
        <LTR style={{ fontSize: 13, fontWeight: 800, color: UI.text.primary, fontFamily: FONT.latin }}>{dateNumeric(ev.date, { year: false })}</LTR>
      </div>
      <span style={{ display: "flex", flexShrink: 0 }}><Icon name={meta.icon} size={16} color={meta.color}/></span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div dir="auto" style={{ fontSize: 14, fontWeight: 700, color: UI.text.primary, lineHeight: 1.4 }}>{ev.title}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: meta.color }}>{ev.kindLabel || meta.label}</span>
          {ev.time && <span style={{ fontSize: 12, color: UI.text.muted }}>· {ev.time}</span>}
          {ev.type === "macro" && ev.source && <span style={{ fontSize: 12, color: UI.text.muted }}>· <LTR>{ev.source}</LTR></span>}
          {sym && <HoldingBadge symbol={sym} portfolio={portfolio} watchlist={watchlist} isHe={isHe}/>}
        </div>
      </div>
      {sym && (
        <button onClick={() => openStock(sym)}
          style={{ flexShrink: 0, background: UI.fill.subtle, border: `1px solid ${UI.line.strong}`, borderRadius: T.radius.md, padding: "4px 10px", fontSize: 12, fontWeight: 800, color: UI.text.primary, cursor: "pointer", minHeight: 32 }}>
          <LTR style={{ fontFamily: FONT.latin }}>{sym}</LTR> ↗
        </button>
      )}
    </div>
  );
}

export default function MarketWeekCard({ portfolio = [], watchlist = [], lang = "he", bare = false }) {
  const isHe = lang !== "en";
  const [events, setEvents] = useState(null);  // null = loading
  const [failed, setFailed] = useState(false);

  const userSymbols = useMemo(
    () => [...portfolio.map(p => p.symbol), ...watchlist.map(w => w.symbol)].filter(Boolean),
    [portfolio, watchlist]
  );
  const symbolsKey = userSymbols.join(",");

  useEffect(() => {
    let alive = true;
    setFailed(false);
    const { from, to } = range7();
    fetchCalendarEvents({ from, to, userSymbols })
      .then(all => { if (alive) setEvents(all.filter(e => e.type === "earnings" || e.type === "ipo" || e.type === "macro")); })
      .catch(() => { if (alive) { setFailed(true); setEvents([]); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolsKey]);

  const sorted = useMemo(() => {
    if (!Array.isArray(events)) return [];
    // user-held first, then by date
    return [...events].sort((a, b) => (Number(!!b.mine) - Number(!!a.mine)) || a.date.localeCompare(b.date));
  }, [events]);

  return (
    <div style={bare ? null : { background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: "16px 18px" }}>
      <div style={{ fontSize: bare ? 15 : 16, fontWeight: 800, color: UI.text.primary, fontFamily: FONT.heading }}>מתרחש בשוק השבוע</div>
      <div style={{ fontSize: 13, color: UI.text.muted, marginTop: 2, marginBottom: 12 }}>
        דוחות · הנפקות · פרסומים כלכליים · <LTR className="si-num">7</LTR> הימים הקרובים
      </div>

      {events === null ? (
        <Loading>{[1, 2, 3].map(i => <Skeleton key={i} h={46} radius={8} mb={8}/>)}</Loading>
      ) : failed ? (
        <div style={{ fontSize: 14, color: UI.text.secondary, textAlign: "center", padding: "14px" }}>לא הצלחנו לטעון כרגע</div>
      ) : sorted.length === 0 ? (
        <div style={{ fontSize: 14, color: UI.text.muted, textAlign: "center", padding: "14px" }}>
          אין דוחות, הנפקות או פרסומים כלכליים מתוזמנים ב-<LTR className="si-num">7</LTR> הימים הקרובים במקורות הזמינים
        </div>
      ) : (
        <div>
          {sorted.slice(0, 20).map(ev => (
            <Row key={ev.id} ev={ev} portfolio={portfolio} watchlist={watchlist} isHe={isHe}/>
          ))}
        </div>
      )}

      <div style={{ fontSize: 12, color: UI.text.muted, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${UI.line.hairline}`, lineHeight: 1.6 }}>
        דוחות והנפקות: <LTR>Finnhub</LTR> · פרסומים כלכליים: לוח הפרסומים של <LTR>FRED</LTR> ולוח הישיבות של <LTR>Federal Reserve</LTR>
      </div>
    </div>
  );
}
